import { Date, UserDisplayName } from "@/components";
import { useEffect } from "react";
import { useGetUsersByIdsMutation } from "@/services/user";
import { Pager } from "@/sharedStyled";
import { Button, Flex, Table, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faHistory, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { NumberParam, useQueryParams } from "use-query-params";
import { IProjectIssue } from "../interfaces";
import { SeverityTag } from "./SeverityTag";
import { LinkedToRiskTag } from "./LinkedToRiskTag";
import { PrioritySeverityTag } from "./PrioritySeverityTag";
import { useGetProjectIssueCategoriesQuery, useGetProjectIssueStatusesQuery } from "@/services";

interface IProps {
  data?: IProjectIssue[];
  totalCount?: number;
  isFetching: boolean;
  canUpdate: boolean;
  onUpdate: (projectIssue: IProjectIssue) => void;
  canDelete: boolean;
  onDelete: (projectIssueId: string) => void;
  onViewHistory: (projectIssueId: string) => void;
}

export const IssuesTable = ({ data, totalCount, isFetching, canUpdate, onUpdate, canDelete, onDelete, onViewHistory }: IProps) => {
  const queryPrefix = 'i_';
  const [queryParams, setQueryParams] = useQueryParams({
    [queryPrefix + 'page']: NumberParam,
  });

  const intl = useIntl();

  const { data: projectIssueStatuses } = useGetProjectIssueStatusesQuery(null);
  const { data: projectIssueCategories } = useGetProjectIssueCategoriesQuery(null);

  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    if (data && data.length > 0) {
      const ownerIds = data.map(risk => risk.ownerId).filter(Boolean);
      if (ownerIds.length > 0) {
        getUsersByIds(ownerIds);
      }
    }
  }, [data, getUsersByIds]);

  const getUserDisplay = (userId: string) => {
    if (!userId) return null;

    const userData = usersByIds?.find(({ id }: { id: string }) => id === userId);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData?.displayName}
        userAccountEnabled={userData?.azureAdAccountEnabled}
        user={userData}
      />
    );
  };


  const columns = [
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.issue" }),
      dataIndex: "description",
      render: (description: string, record: IProjectIssue) => (
        <>
          {description}
          { !!record.projectRiskPublicId && <>{' '}<LinkedToRiskTag /> </> }
        </>
      ),
      width: 280,
    },
   {
      title: intl.formatMessage({ id: "risksAndIssues.columns.impact" }),
      dataIndex: "impactSeverityLevel",
      render: (impact: number, record: IProjectIssue) => <SeverityTag value={impact} severityLevel={record.impactSeverityLevel} />,
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.probability" }),
      dataIndex: "probabilitySeverityLevel",
      render: (probability: number, record: IProjectIssue) => <SeverityTag value={probability} severityLevel={record.probabilitySeverityLevel} />,
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.priority" }),
      dataIndex: "prioritySeverityLevelValue",
      render: (priority: number, record: IProjectIssue) => <PrioritySeverityTag value={priority} severityLevel={record.prioritySeverityLevel} />,
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.status" }),
      dataIndex: "projectIssueStatusId",
      render: (statusId: number) => {
        const statusName = projectIssueStatuses?.find(option => option.id === statusId)?.name;
        return <span>{statusName}</span>;
      },
      width: 110,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.category" }),
      dataIndex: "projectIssueCategoryId",
      render: (categoryId: number) => {
        const categoryName = projectIssueCategories?.find(category => category.id === categoryId)?.name;
        return <span>{categoryName}</span>;
      },
      width: 110,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.owner" }),
      dataIndex: "ownerId",
      render: getUserDisplay,
      width: 140,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.rootCause" }),
      dataIndex: "rootCause",
      render: (rootCause: string) => <Tooltip title={rootCause} className="line-clamp-3">{rootCause}</Tooltip>,
      width: 175,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.nextSteps" }),
      dataIndex: "nextSteps",
      render: (nextSteps: string) => <Tooltip title={nextSteps} className="line-clamp-3">{nextSteps}</Tooltip>, 
      width: 175,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.dateIdentified" }),
      dataIndex: "dateIdentified",
      render: (data: string) => <Date value={data} />,
      width: 130,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.target" }),
      dataIndex: "targetCompletionDate",
      render: (data: string) => <Date value={data} />,
      width: 95,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.history" }),
      dataIndex: "history",
      render: (history: string) => <Tooltip title={history} className="line-clamp-3">{history}</Tooltip>,
      width: 175,
    },
    ...((canUpdate || canDelete)
      ? [
          {
            title: intl.formatMessage({ id: "general.actions" }),
            width: 90,
            fixed: 'right' as 'right' | 'left', // Adding the 'as' assertion to resolve TypeScript type issue
            render: (_: unknown, record: IProjectIssue) => (
              <Flex>
                {canUpdate &&
                  <Button
                    type="link"
                    data-test-id={`project-issue-update-${record.projectIssuePublicId}`}
                    onClick={() => onUpdate(record)}
                    icon={<FontAwesomeIcon icon={faEdit} />}
                  />}
                {canDelete &&
                  <Button
                    type="link"
                    data-test-id={`project-issue-delete-${record.projectIssuePublicId}`}
                    onClick={() => onDelete(record.projectIssuePublicId)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />}
                  <Button
                      type="link"
                      data-test-id={`project-issue-history-${record.projectIssuePublicId}`}
                      onClick={() => onViewHistory(record.projectIssuePublicId)}
                      icon={<FontAwesomeIcon icon={faHistory} />}
                  />
              </Flex>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        data-test-id="project-issues-table"
        columns={columns}
        dataSource={data}
        loading={isFetching}
        rowKey={({ projectIssuePublicId }) => projectIssuePublicId}
        pagination={false}
        scroll={{ x: 1840 }}
      />
      <Pager
        current={queryParams[queryPrefix + 'page'] || undefined}
        total={totalCount}
        showSizeChanger={false}
        onChange={(page) => setQueryParams({ [queryPrefix + 'page']: page })}
        data-test-id="projects-issues-table-pagination"
      />
    </>
  );
};
